import React from 'react'
import { FormInput } from '../ui'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { searchControlCatalog, type ControlCatalogEntry } from '../../services/informationSystemPhase13Api'

interface Props {
  standardCode: string
  controlId: string
  controlTitle: string
  onSelect: (entry: { controlId: string; controlTitle: string }) => void
  onControlIdChange: (v: string) => void
  onControlTitleChange: (v: string) => void
}

export default function CanonicalControlPicker({
  standardCode,
  controlId,
  controlTitle,
  onSelect,
  onControlIdChange,
  onControlTitleChange,
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [query, setQuery] = React.useState('')
  const [options, setOptions] = React.useState<ControlCatalogEntry[]>([])
  const [catalogAvailable, setCatalogAvailable] = React.useState(true)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!token || !standardCode.trim()) {
      setOptions([])
      return
    }
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await searchControlCatalog(token, { standardCode, query, limit: 30 })
        setCatalogAvailable(data.catalogAvailable)
        setOptions(data.controls || [])
      } catch {
        setCatalogAvailable(false)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [token, standardCode, query])

  return (
    <div className="space-y-2 border rounded p-3 bg-gray-50">
      <p className="text-xs font-medium text-gray-700">{t('assets.controlPicker.title', 'Control mapping')}</p>
      {!catalogAvailable ? (
        <p className="text-xs text-amber-700">{t('assets.controlPicker.manualFallback', 'Manual entry available')}</p>
      ) : null}
      {standardCode.trim() ? (
        <>
          <FormInput
            placeholder={t('assets.controlPicker.search', 'Search controls…')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {loading ? <p className="text-xs text-gray-500">{t('common.loading')}</p> : null}
          {options.length > 0 ? (
            <ul className="max-h-32 overflow-y-auto text-sm border rounded bg-white divide-y">
              {options.map((o) => (
                <li key={`${o.canonicalCode}-${o.controlId}`}>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1 hover:bg-blue-50"
                    onClick={() => {
                      onSelect({ controlId: o.controlId, controlTitle: o.controlTitle })
                      onControlIdChange(o.controlId)
                      onControlTitleChange(o.controlTitle)
                    }}
                  >
                    <span className="font-medium">{o.controlId}</span>
                    {o.controlTitle ? <span className="text-gray-500"> — {o.controlTitle}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : catalogAvailable && !loading ? (
            <p className="text-xs text-gray-500">{t('assets.controlPicker.noResults', 'No controls match search')}</p>
          ) : null}
        </>
      ) : (
        <p className="text-xs text-gray-500">{t('assets.controlPicker.selectStandardFirst', 'Select a standard first')}</p>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        <FormInput
          placeholder={t('assets.grc.controlId', 'Control ID')}
          value={controlId}
          onChange={(e) => onControlIdChange(e.target.value)}
        />
        <FormInput
          placeholder={t('assets.grc.controlTitle', 'Control title')}
          value={controlTitle}
          onChange={(e) => onControlTitleChange(e.target.value)}
        />
      </div>
    </div>
  )
}
